<template>
  <div class="flex">
    <div class="flex flex-col items-center">
      <ReviewInteractionStatusIcon :type="ReviewInteractionType.REMINDER_CHECK" />
      <div class="mt-4 h-full border-l border-solid border-main" />
    </div>
    <div class="mt-0.5 ml-2">
      <div class="mb-3 text-body-s text-subtlest">
        {{ t('Reviews.product.review.history.datetime', { dateTime: formattedDateTime }) }}:
        <span class="text-body-s-bold">{{ t('Reviews.product.review.history.reminderSent') }}</span>
      </div>
      <div v-if="!reminder.automatic" class="mb-4 text-body-s text-subtle">
        {{ t('Reviews.product.review.history.sender', { senderName }) }}
      </div>
      <div v-if="hint" class="mb-4 text-body-m">{{ hint }}</div>
      <div v-if="reminder.message" class="bg-surface-elevation-4 p-4" v-html="nl2br(reminder.message)" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { nl2br } from '@ramp106/omrjs-core-ui'
import { differenceInDays } from 'date-fns'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewInteractionStatusIcon from '@/components/ReviewsCommon/ReviewInteractionStatus/ReviewInteractionStatusIcon.vue'
import { ReviewInteractionType } from '@/components/ReviewsCommon/ReviewInteractionStatus/types'
import { useUserQuery } from '@/gql/myomr'

import type { SurveyResponseReminder } from './types'

const REMINDER_RESEND_DAYS = 45

const props = defineProps<{
  authorName?: string
  reminder: SurveyResponseReminder
}>()

const { t, d } = useI18n()

const { result } = useUserQuery({ userId: String(props.reminder.userId) })

const senderName = computed(() => {
  const user = result.value?.user

  if (user) {
    return [user.firstName, user.lastName].filter((x) => !!x).join(' ')
  }
  return t('Reviews.product.review.history.unknownSender')
})

const formattedDateTime = computed(() => d(props.reminder.sentAt || props.reminder.createdAt, 'dateTimeShort'))

const daysLeft = computed(() => {
  if (!props.reminder.sentAt) {
    return 0
  }
  return differenceInDays(new Date(props.reminder.sentAt), new Date()) + REMINDER_RESEND_DAYS
})

const hint = computed(() => {
  const authorName = props.authorName || t('Reviews.product.review.history.unknownAuthor')

  if (props.reminder.automatic) {
    return t('Reviews.product.review.history.automaticReminderHint', { authorName })
  } else if (daysLeft.value > 0) {
    return t('Reviews.product.review.history.reminderHint', {
      authorName,
      daysLeft: daysLeft.value,
    })
  }

  return ''
})
</script>
